import React, { useCallback, useEffect, useRef, useState } from 'react';

import Check from '@videoblocks/react-icons/Check';

import CopySvg from '../../../../../../../common/images/icons/copy.svg';
import { Text } from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import Tooltip from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Tooltip/Tooltip';

interface PromptUsedBoxProps {
  promptText: string;
}

export default function PromptUsedBox({ promptText }: PromptUsedBoxProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(promptText);
    } catch {
      // Fallback for browsers that don't support navigator.clipboard or lack HTTPS/permission
      const textarea = document.createElement('textarea');
      textarea.value = promptText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setCopied(false);
    }, 1500);
  }, [promptText]);

  return (
    <div
      className="relative w-full rounded-xl pr-3"
      style={{
        backgroundColor: '#343f4a',
      }}
    >
      <div
        className="prompt-used-scroll overflow-y-auto flex items-start gap-2.5 pt-5 pr-3 pb-6 pl-5"
        style={{ maxHeight: '108px' }}
      >
        <p className="flex-1 min-w-0 m-0">
          <Text fontSize="sm" fontWeight="semibold" asChild>
            <span className="text-gray-150">Prompt Used: </span>
          </Text>
          <Text fontSize="sm" fontWeight="regular" asChild>
            <span className="text-gray-150">{promptText}</span>
          </Text>
        </p>

        <div className="sticky top-0 shrink-0">
          <Tooltip
            open={isHovered || copied}
            title={copied ? 'Copied' : 'Copy prompt'}
            variant="light"
            trigger={
              <button
                type="button"
                className="flex items-center justify-center cursor-pointer w-10 h-10 rounded-full border-0"
                style={{
                  backgroundColor: isHovered || copied ? '#626c78' : '#505a66',
                }}
                onClick={handleCopy}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                aria-label="Copy prompt text"
              >
                {copied ? (
                  <Check width={16} height={16} fill="white" />
                ) : (
                  <img src={CopySvg} width={16} height={16} alt="" />
                )}
              </button>
            }
          />
        </div>
      </div>

      <div
        className="absolute inset-0 right-6 rounded-l-xl pointer-events-none"
        style={{
          borderTopWidth: '20px',
          borderBottomWidth: '24px',
          borderColor: '#343f4a',
        }}
      />
    </div>
  );
}
